import React, {useState, useRef, useEffect} from 'react';
import {View, FlatList, ScrollView, TextInput, StyleSheet} from 'react-native';
import SlideModal from '@/Atoms/SlideModal';
import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import CCButton from '@/Atoms/CCButton';
import CCColors, {updateColorWithOpacity} from '@/Utils/CCColors';
import {FontSizes, vs} from '@/Utils/Dimensions';
import Feather from '@expo/vector-icons/Feather';
import Ionicons from '@expo/vector-icons/Ionicons';
import Octicons from '@expo/vector-icons/Octicons';
import Loader from '@/Atoms/CCLoader';
import CCTextInput from '@/Atoms/CCTextInput';
import Spacer from '@/Atoms/Spacer';
import Divider from '@/Atoms/Divider/Divider';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import Icon from 'react-native-vector-icons/MaterialIcons';
import {getFormattedTimeZone} from '@/Utils/CommonUtils';

dayjs.extend(utc);
dayjs.extend(timezone);

const weekdays = Array.from({length: 7}, (_, i) =>
  dayjs().day(i).format('dddd'),
);

// function to get nearest date for a weekday
function getNearestDateForDay(dayCode: number) {
  const today = dayjs();
  const todayDay = today.day();

  let diff = dayCode - todayDay;
  if (diff < 0) {
    diff += 7; // move to next week if day already passed
  }
  return today.add(diff, 'day');
}

// function to get time slots for the day
function getDayTimes(startTime: number, endTime: number, intervalMinutes = 60) {
  const start = dayjs().hour(startTime).minute(0).second(0);
  const end = dayjs().hour(endTime).minute(0).second(0);

  const times: string[] = [];
  let current = start;

  while (current.isBefore(end) || current.isSame(end)) {
    times.push(current.format('hh:mm A'));
    current = current.add(intervalMinutes, 'minute');
  }

  return times;
}

const scrollStep = 5;

interface Slot {
  id?: string;
  start_time: string;
  end_time: string;
  is_available?: boolean;
  blocked_reason?: string;
}

export interface AvailableSlots {
  [date: string]: Slot[];
}

interface RescheduleModalProps {
  visible: boolean;
  onClose: () => void;
  isLoading?: boolean;
  availableSlots?: AvailableSlots;
  isHost?: boolean;
  callId?: string;
  onBook?: (data: any) => void;
  onReschedule: (data: any) => void;
  onMentorCallRequest?: (data: any) => void;
  bookCall?: boolean;
  mobile?: string;
  userTimezone?: string;
  apiResponse?: {
    error?: string;
    message?: string;
    result?: any;
  } | null;
}

const RescheduleModal: React.FC<RescheduleModalProps> = ({
  visible,
  onClose,
  isLoading = false,
  availableSlots = {},
  isHost = false,
  callId,
  onBook,
  onReschedule,
  onMentorCallRequest,
  bookCall,
  mobile = '',
  userTimezone = '',
  apiResponse,
}) => {
  const containerRef = useRef<any>(null);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedTime, setSelectedTime] = useState('');
  const [showReason, setShowReason] = useState(false);
  const [rescheduleComment, setRescheduleComment] = useState('');
  const [didntFind, setDidntFind] = useState(false);
  const [currentDateScrollIndex, setCurrentDateScrollIndex] = useState(0);
  const [callRequestPayload, setCallRequestPayload] = useState({reason: ''});
  const [studentMobile, setStudentMobile] = useState('');

  useEffect(() => {
    if (visible && availableSlots) {
      const firstDate = Object.keys(availableSlots)?.[0];
      if (firstDate) {
        setSelectedDate(firstDate);
      }
    }
  }, [visible, availableSlots]);

  useEffect(() => {
    if (!visible) {
      setSelectedTime('');
      setShowReason(false);
      setDidntFind(false);
      setRescheduleComment('');
      setCallRequestPayload({reason: ''});
      setCurrentDateScrollIndex(0);
    }
  }, [visible]);

  const scrollToElement = (index: number) => {
    containerRef.current?.scrollToIndex({index, animated: true});
  };

  const handlePrevNext = (type: 'prev' | 'next', max: number) => {
    let index = 0;
    if (type === 'prev' && currentDateScrollIndex > 0) {
      index = currentDateScrollIndex - scrollStep;
    } else if (type === 'next') {
      index = currentDateScrollIndex + scrollStep;
    }
    if (index > max) {
      index = max;
    }
    setCurrentDateScrollIndex(index);
    scrollToElement(index);
  };

  const handleSubmit = () => {
    if (didntFind && onMentorCallRequest) {
      onMentorCallRequest({
        type: callId ? 'reschedule' : 'booking',
        call_id: callId,
        requested_slot: selectedTime,
        reason: callRequestPayload.reason,
      });
    } else if (bookCall && onBook) {
      onBook({
        mobile_number: isHost ? studentMobile : mobile,
        start_time: selectedTime,
        timezone: userTimezone,
        single_meeting: isHost,
      });
    } else {
      // Convert selected local time to IST before sending to backend
      const effectiveTimezone =
        userTimezone ||
        Intl.DateTimeFormat().resolvedOptions().timeZone ||
        'Asia/Kolkata';

      let istStartTime = selectedTime;
      try {
        const local = dayjs.tz(selectedTime, effectiveTimezone);
        const ist = local.tz('Asia/Kolkata');
        istStartTime = ist.format('YYYY-MM-DDTHH:mm:ss');
      } catch (e) {
        istStartTime = selectedTime;
      }

      onReschedule({
        call_id: callId!,
        start_time: istStartTime,
        comment: rescheduleComment,
      });
    }
  };

  const isDisabled =
    isLoading || (showReason ? !rescheduleComment : !selectedTime);

  return (
    <SlideModal
      visible={visible}
      showHeader
      showCloseIcon
      customStyle={{
        minWidth: 500,
        width: 500,
        minHeight: 400,
      }}
      modalHeaderTitle={
        <Box type="row-center" gap={3}>
          <Ionicons
            name={showReason ? 'checkmark-circle' : 'calendar-outline'}
            size={showReason ? 18 : 15}
            color={showReason ? '#3DAB9E' : '#4D3F37'}
          />
          <CCText>Slot duration</CCText>
          {callId && !didntFind && (
            <>
              <CCText color={CCColors.Grey.Grey}>{' - '}</CCText>
              <Ionicons
                name="document-text-outline"
                size={15}
                color={updateColorWithOpacity(
                  CCColors.Primary.Brown,
                  showReason ? 1 : 0.5,
                )}
              />
              <CCText
                color={updateColorWithOpacity(
                  CCColors.Primary.Brown,
                  showReason ? 1 : 0.5,
                )}>
                Reason for Reschedule
              </CCText>
            </>
          )}
        </Box>
      }
      onClose={onClose}>
      <Loader
        loading={isLoading}
        bgStyle={{position: 'absolute', top: -60, left: 0, height: '120%'}}
      />
      <View style={{maxHeight: 350}}>
        <ScrollView>
          {isHost && !callId && (
            <>
              <CCText>Student Mobile Number</CCText>
              <CCTextInput
                onChangeText={value => {
                  setStudentMobile(value);
                }}
              />
              <Spacer spacing={10} />
            </>
          )}
          {callId && showReason ? (
            <Box>
              <CCText fontSize={FontSizes[16]}>Reason of Reschedule</CCText>
              <Spacer spacing={10} />
              <TextInput
                style={styles.textArea}
                multiline
                placeholder="Add Reason for Reschedule"
                value={rescheduleComment}
                placeholderTextColor={CCColors.Grey.Grey}
                onChangeText={text => {
                  setRescheduleComment(text);
                }}
              />
            </Box>
          ) : availableSlots && Object.keys(availableSlots).length > 0 ? (
            <>
              <Box type="row-center-between">
                <CCText fontSize={FontSizes[16]}>
                  Select date and time slot
                </CCText>
                <Box type="row-center" gap={10}>
                  <Feather
                    name="chevron-left"
                    disabled={currentDateScrollIndex === 0}
                    onPress={() =>
                      handlePrevNext(
                        'prev',
                        Object?.keys(availableSlots || [])?.length,
                      )
                    }
                  />
                  <Feather
                    name="chevron-right"
                    onPress={() =>
                      handlePrevNext(
                        'next',
                        Object?.keys(availableSlots || [])?.length,
                      )
                    }
                  />
                </Box>
              </Box>
              <Spacer spacing={10} />
              <FlatList
                ref={containerRef}
                horizontal
                data={isHost || callId ? Object.keys(availableSlots) : weekdays}
                renderItem={({item}) => {
                  let formattedItem = item;
                  if (!isHost && !callId) {
                    const code = weekdays.indexOf(item);
                    formattedItem =
                      getNearestDateForDay(code).format('YYYY-MM-DD');
                  }
                  return (
                    <Box
                      key={formattedItem}
                      style={[
                        styles.dateCard,
                        {
                          marginRight: 10,
                          width: 80,
                          height: 'auto',
                          borderColor:
                            selectedDate === formattedItem
                              ? '#4D3F37'
                              : '#26232233',
                        },
                      ]}
                      onPress={() => setSelectedDate(formattedItem)}>
                      <Box
                        style={[
                          styles.monthbox,
                          {
                            backgroundColor:
                              selectedDate === formattedItem
                                ? CCColors.Primary.Dark
                                : CCColors.Primary.Light,
                          },
                        ]}>
                        <CCText
                          fontSize={FontSizes[16]}
                          color={
                            selectedDate === formattedItem
                              ? CCColors.White
                              : CCColors.TextColor.Dark
                          }
                          textAlign="center"
                          fontWeight="bold">
                          {isHost || callId
                            ? dayjs(formattedItem).format('MMM')
                            : dayjs(formattedItem).format('ddd')}
                        </CCText>
                      </Box>
                      {(isHost || callId) && (
                        <CCText
                          textAlign="center"
                          fontWeight="bold"
                          fontSize={FontSizes[16]}>
                          {dayjs(formattedItem).get('date')}
                        </CCText>
                      )}
                    </Box>
                  );
                }}
                keyExtractor={(item, index) => index.toString()}
              />
              <Spacer spacing={20} />
              {selectedDate && (
                <>
                  <Box type="row-center-between">
                    {didntFind ? (
                      <CCText>Find the Slot which you are willing to</CCText>
                    ) : (
                      <>
                        <Box type="row-flex-start" gap={10}>
                          <Octicons name="dot-fill" color={CCColors.Green} />
                          <CCText>
                            {availableSlots?.[selectedDate]?.length || 'No'}{' '}
                            slots available
                          </CCText>
                        </Box>
                        {/* <CCText
                              color={CCColors.Green}
                              onPress={() => setDidntFind(true)}>
                              Didn't find the slot ?
                            </CCText> */}
                      </>
                    )}
                  </Box>
                  <Spacer spacing={5} />
                  <Box flexWrap="wrap" type="row-flex-start" gap={5}>
                    {(didntFind
                      ? getDayTimes(9, 21, 30)
                          .filter(
                            x =>
                              !(availableSlots?.[selectedDate] ?? [])
                                ?.map((item: Slot) =>
                                  dayjs(item.start_time).format('hh:mm A'),
                                )
                                .includes(x),
                          )
                          .map(time => ({
                            start_time: dayjs(`${selectedDate} ${time}`).format(
                              'YYYY-MM-DDTHH:mm:ss',
                            ),
                          }))
                      : availableSlots?.[selectedDate]
                    )?.map((item: any) => (
                      <Box
                        key={item.original_start_time}
                        style={[
                          styles.dateCard,
                          {
                            width: 80,
                            backgroundColor:
                              selectedTime === item.original_start_time
                                ? CCColors.Primary.Dark
                                : CCColors.White,
                          },
                        ]}
                        onPress={() => {
                          setSelectedTime(item.original_start_time);
                        }}>
                        <CCText
                          fontSize={FontSizes[12]}
                          color={
                            selectedTime === item.original_start_time
                              ? CCColors.White
                              : CCColors.TextColor.Dark
                          }>
                          {dayjs(item.start_time).format('hh:mm A')}
                        </CCText>
                      </Box>
                    ))}
                  </Box>
                </>
              )}

              {didntFind && (
                <>
                  <Spacer spacing={10} />
                  <CCText>Reason for this Slot</CCText>
                  <Spacer spacing={10} />
                  <TextInput
                    style={styles.textArea}
                    multiline
                    placeholder="Reason for this Slot"
                    value={callRequestPayload?.reason || ''}
                    placeholderTextColor={CCColors.Grey.Grey}
                    onChangeText={text => {
                      setCallRequestPayload(prev => ({
                        ...prev,
                        reason: text,
                      }));
                    }}
                  />
                </>
              )}
              <Spacer spacing={20} />
              <Divider />
              {!isHost && !callId && (
                <>
                  <Spacer spacing={10} />
                  <CCText color={CCColors.Grey.Grey}>
                    Note: The mentor session will be scheduled for the entire
                    duration based on your selected date and time.
                  </CCText>
                </>
              )}
            </>
          ) : (
            <View style={[styles.limitInfoBox, styles.limitWarningBox]}>
              <Icon name={'error'} size={20} color={CCColors.Red} />
              <View style={{flex: 1, marginLeft: 10}}>
                <CCText fontSize={FontSizes[11]} color={CCColors.Red}>
                  No Slots Available
                </CCText>
              </View>
            </View>
          )}
        </ScrollView>
      </View>
      <Spacer spacing={20} />
      {didntFind ? (
        <Box type="row-flex-end">
          <CCButton onPress={handleSubmit}>Submit Request</CCButton>
        </Box>
      ) : (
        <Box type="row-center-between">
          <CCText>{getFormattedTimeZone()}</CCText>
          {(showReason || bookCall) && (
            <CCButton disabled={isDisabled} onPress={handleSubmit}>
              {bookCall ? 'Book Call' : 'Reschedule Call'}
            </CCButton>
          )}
          {callId && selectedTime && !showReason && (
            <CCButton onPress={() => setShowReason(true)}>Next</CCButton>
          )}
        </Box>
      )}
      {apiResponse && (apiResponse.message || apiResponse?.error) ? (
        <View
          style={[
            styles.limitInfoBox,
            apiResponse?.error ? styles.limitBreachBox : styles.limitNormalBox,
          ]}>
          <Icon
            name={apiResponse?.error ? 'error' : 'info'}
            size={20}
            color={apiResponse?.error ? CCColors.Red : CCColors.Green}
          />
          <View style={{flex: 1, marginLeft: 10}}>
            <CCText
              fontSize={FontSizes[11]}
              color={apiResponse?.error ? CCColors.Red : CCColors.Green}>
              {apiResponse?.error ? apiResponse?.error : apiResponse?.message}
            </CCText>
          </View>
        </View>
      ) : (
        <></>
      )}
    </SlideModal>
  );
};

const styles = StyleSheet.create({
  dateCard: {
    backgroundColor: CCColors.White,
    borderRadius: 8,
    padding: vs(4),
    width: 50,
    height: 50,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#26232233',
    shadowColor: '#4D3F370D',
    shadowOffset: {
      width: 0,
      height: 6,
    },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 8,
    marginVertical: vs(5),
  },
  monthbox: {
    backgroundColor: CCColors.Primary.Light,
    borderRadius: 4,
    width: '100%',
    marginBottom: vs(4),
  },
  textArea: {
    borderWidth: 1,
    borderColor: CCColors.TextColor.Grey,
    borderRadius: 8,
    padding: 10,
    minHeight: 100,
    marginBottom: 20,
    textAlignVertical: 'top',
  },
  limitInfoBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    padding: 12,
    borderRadius: 8,
    marginTop: 12,
    borderWidth: 1,
  },
  limitNormalBox: {
    backgroundColor: '#E3F2FD',
    borderColor: '#90CAF9',
  },
  limitWarningBox: {
    backgroundColor: '#FFF9E6',
    borderColor: '#FFE082',
  },
  limitBreachBox: {
    backgroundColor: '#FFEBEE',
    borderColor: '#EF5350',
  },
  limitStats: {
    flexDirection: 'row',
    marginTop: 8,
    gap: 12,
  },
});

export default RescheduleModal;
